/**
 * Form state for the "Connected MCP servers" Settings card: what the add/edit form holds, which
 * fields an edit sends, and the one-line status shown under each server. Presentation only; the
 * Worker validates and enforces everything (`flux_agent/mcp_servers.rs`).
 *
 * @module apps/web/src/components/settings/mcpServerForm
 */

import type { McpAuthKind, McpServer, McpServerInput } from '@/lib/fluxAgentMcpApi';

/** Values of the add/edit form. The secret is never prefilled; empty means "keep the stored one". */
export interface McpServerFormState {
  /** Display name. */
  name: string;
  /** `https://` address. */
  url: string;
  /** Credential kind. */
  authKind: McpAuthKind;
  /** Custom header name for `header`. */
  headerName: string;
  /** Token or header value typed in this form. */
  secret: string;
}

/** An empty add form. */
export function emptyForm(): McpServerFormState {
  return { name: '', url: '', authKind: 'none', headerName: '', secret: '' };
}

/** An edit form for `server`, with the secret left blank. */
export function formFromServer(server: McpServer): McpServerFormState {
  return {
    name: server.name,
    url: server.url,
    authKind: server.authKind,
    headerName: server.authHeaderName ?? '',
    secret: '',
  };
}

/**
 * The request body for the form. A create sends every field. An edit sends only changed fields,
 * and sends `auth` only when the person typed a new secret, changed the kind, or chose "None"
 * for a server that has a credential, so saving a name change never touches the credential.
 */
export function formToInput(form: McpServerFormState, existing?: McpServer): McpServerInput {
  const name = form.name.trim();
  const url = form.url.trim();
  const auth =
    form.authKind === 'none'
      ? { kind: 'none' as const }
      : form.authKind === 'bearer'
        ? { kind: 'bearer' as const, value: form.secret }
        : { kind: 'header' as const, headerName: form.headerName.trim(), value: form.secret };
  if (!existing) return form.authKind === 'none' ? { name, url } : { name, url, auth };
  const input: McpServerInput = {};
  if (name !== existing.name) input.name = name;
  if (url !== existing.url) input.url = url;
  const kindChanged = form.authKind !== existing.authKind;
  const headerChanged =
    form.authKind === 'header' && form.headerName.trim() !== (existing.authHeaderName ?? '');
  if (
    form.authKind === 'none' ? existing.hasCredential : kindChanged || headerChanged || form.secret
  )
    input.auth = auth;
  return input;
}

/** Tone and text of a server's status line. */
export interface McpServerStatus {
  /** `ok` after a clean discovery, `error` when the last attempt failed, `never` before any. */
  tone: 'ok' | 'error' | 'never';
  /** One sentence for the person. */
  text: string;
}

/** The status line under a server row. */
export function serverStatus(server: McpServer): McpServerStatus {
  if (server.lastError) return { tone: 'error', text: server.lastError };
  if (!server.lastDiscoveredAt) return { tone: 'never', text: 'Not tested yet.' };
  const count = server.tools.length;
  const tools = `${count} tool${count === 1 ? '' : 's'}`;
  const truncated = server.toolsTruncated ? ' (only the first 50 are used)' : '';
  return { tone: 'ok', text: `Connected · ${tools}${truncated}` };
}
